package app.footyguide.web.components.club

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import app.footyguide.core.locale.AppStrings
import app.footyguide.core.theme.Club
import app.footyguide.web.components.CrestSizes
import app.footyguide.web.components.Icons
import app.footyguide.web.components.SafeImg
import app.footyguide.web.data.StandingRow
import app.footyguide.web.data.StandingZone
import app.footyguide.web.route.WebRoutes

/**
 * Standings table view highlighting the club and linking every row to /time/{row.teamId}.
 */
@Composable
fun ClubStandingsTable(
    club: Club,
    standings: List<StandingRow>,
    onNavigate: (String) -> Unit,
    strings: AppStrings? = null
) {
    Div({ style { padding(0.px, 16.px, 20.px) } }) {
        if (standings.isEmpty()) {
            Div({
                style {
                    padding(32.px, 20.px)
                    backgroundColor(Color("#FFFFFF"))
                    border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                    borderRadius(16.px)
                    property("text-align", "center")
                    color(Color("#71717A"))
                    fontSize(13.px)
                    fontFamily("Inter, sans-serif")
                }
            }) {
                Text(strings?.standingsUnavailableTitle ?: app.footyguide.core.locale.LocalStrings.current.standingsUnavailableTitle)
            }
            return@Div
        }
        Div({
            style {
                backgroundColor(Color("#FFFFFF"))
                border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                borderRadius(16.px)
                overflow("hidden")
                property("box-shadow", "0 2px 8px rgba(22, 24, 29, 0.04)")
            }
        }) {
            // TABLE HEADER
            Div({
                style {
                    display(DisplayStyle.Flex)
                    padding(10.px, 12.px)
                    backgroundColor(Color("#FAFAF7"))
                    property("border-bottom", "1px solid #E7E7E1")
                    fontSize(11.px)
                    fontWeight(800)
                    fontFamily("Inter, sans-serif")
                    color(Color("#71717A"))
                    property("text-transform", "uppercase")
                }
            }) {
                Span({ style { width(32.px) } }) { Text("#") }
                Span({ style { flex(1) } }) { Text(strings?.tableClub ?: "Clube") }
                Span({ style { width(36.px); property("text-align", "center") } }) { Text(strings?.tablePointsShort ?: "P") }
                Span({ style { width(28.px); property("text-align", "center") } }) { Text(strings?.tablePlayedShort ?: "J") }
                Span({ style { width(28.px); property("text-align", "center") } }) { Text(strings?.tableWonShort ?: "V") }
                Span({ style { width(32.px); property("text-align", "center") } }) { Text(strings?.tableGoalDiffShort ?: "SG") }
            }

            standings.forEach { row ->
                val isHighlighted = row.teamId == club.slug
                val zoneBarColor = when (row.zone) {
                    StandingZone.LIBERTADORES -> "#059669"
                    StandingZone.PRE_LIBERTADORES -> "#10B981"
                    StandingZone.SUL_AMERICANA -> "#0284C7"
                    StandingZone.RELEGATION -> "#DC2626"
                    StandingZone.NEUTRAL -> "transparent"
                }

                Div({
                    onClick { onNavigate(WebRoutes.club(row.teamId)) }
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        padding(9.px, 12.px)
                        property("border-bottom", "1px solid #F4F4F5")
                        backgroundColor(Color(if (isHighlighted) "rgba(var(--accent-fill-rgb, 11, 110, 79), 0.08)" else "#FFFFFF"))
                        property("border-left", if (isHighlighted) "3px solid ${club.c1}" else "3px solid $zoneBarColor")
                        fontSize(13.px)
                        fontFamily("Inter, sans-serif")
                        cursor("pointer")
                    }
                }) {
                    Span({
                        style {
                            width(32.px)
                            fontWeight(700)
                            fontFamily("Sora, sans-serif")
                            fontSize(12.px)
                            color(Color(if (isHighlighted) club.c1 else "#71717A"))
                        }
                    }) {
                        Text("${row.position}")
                    }

                    Div({
                        style {
                            flex(1)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(8.px)
                        }
                    }) {
                        SafeImg(
                            src = row.logoUrl,
                            fallbackIcon = Icons.SHIELD,
                            fallbackSize = CrestSizes.STANDINGS_ROW_FALLBACK,
                            loading = "eager",
                            modifier = {
                                width(CrestSizes.STANDINGS_ROW_PX.px)
                                height(CrestSizes.STANDINGS_ROW_PX.px)
                                property("width", CrestSizes.STANDINGS_ROW_CSS)
                                property("height", CrestSizes.STANDINGS_ROW_CSS)
                                property("object-fit", "contain")
                            }
                        )
                        Span({
                            style {
                                fontWeight(if (isHighlighted) 800 else 600)
                                color(Color(if (isHighlighted) "#16181D" else "#27272A"))
                            }
                        }) {
                            Text(row.name)
                        }
                    }

                    Span({
                        style {
                            width(36.px)
                            fontWeight(800)
                            fontFamily("Sora, sans-serif")
                            fontSize(13.px)
                            color(Color("#16181D"))
                            property("text-align", "center")
                        }
                    }) {
                        Text("${row.points}")
                    }

                    Span({
                        style {
                            width(28.px)
                            color(Color("#71717A"))
                            fontSize(12.px)
                            property("text-align", "center")
                        }
                    }) {
                        Text("${row.played}")
                    }

                    Span({
                        style {
                            width(28.px)
                            color(Color("#71717A"))
                            fontSize(12.px)
                            property("text-align", "center")
                        }
                    }) {
                        Text("${row.wins}")
                    }

                    Span({
                        style {
                            width(32.px)
                            color(Color("#71717A"))
                            fontSize(12.px)
                            property("text-align", "center")
                        }
                    }) {
                        Text("${row.goalDifference}")
                    }
                }
            }
        }
    }
}
